import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Grid, Button, CircularProgress, Alert } from '@mui/material';
import { api } from '../api/client';
import { useDecodedToken } from '../utils/useDecodedToken';

interface Transaction {
    id: number;
    type: {
        id: number;
        code: string;
        value: string;
    };
    date: number[];
    amount: number;
    outstandingLoanBalance: number;
    manuallyReversed: boolean;
}

const RecentTransactions: React.FC = () => {
    const user = useDecodedToken();
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchTransactions();
    }, [user?.accountNumber]);

        const fetchTransactions = async () => {
        setLoading(true);
        try {
            // Use the loan associations endpoint for transactions
            if (user?.accountNumber) {
                // Get client accounts to find loan IDs
                const accountsResponse = await api.get(`/api/clients/${user.accountNumber}/accounts`);
                const loanAccounts = accountsResponse.data.loanAccounts || [];
                
                // Fetch transactions for each loan
                const allTransactions: any[] = [];
                for (const loan of loanAccounts) {
                    try {
                        const loanTransactionsResponse = await api.get(`/api/loans/${loan.id}/associations?associations=transactions`);
                        const loanTransactions = loanTransactionsResponse.data.transactions || [];
                        if (Array.isArray(loanTransactions)) {
                            allTransactions.push(...loanTransactions);
                        }
                    } catch (loanErr: any) {
                        console.error(`Failed to fetch transactions for loan ${loan.id}:`, loanErr);
                    }
                }
                
                setTransactions(allTransactions);
            }
        } catch (err) {
            console.error('Failed to fetch transactions:', err);
            setError('Failed to load transactions. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        fetchTransactions();
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTransactionColor = (type: string) => {
        return type.toUpperCase() === 'DEBIT' ? 'error.main' : 'success.main';
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Recent Transactions</Typography>
                <Button
                    variant="outlined"
                    onClick={handleRefresh}
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {(!transactions || !Array.isArray(transactions) || transactions.length === 0) ? (
                <Typography>No recent transactions found.</Typography>
            ) : (
                <Grid container spacing={2}>
                    {transactions.map((transaction) => (
                        <Grid item xs={12} key={transaction.id}>
                            <Card>
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="subtitle1">
                                                {transaction.type?.value || 'N/A'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                ID: {transaction.id}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <Typography variant="body2" color="text.secondary">
                                                Date
                                            </Typography>
                                            <Typography variant="body1">
                                                {transaction.date ? new Date(transaction.date[0], transaction.date[1] - 1, transaction.date[2]).toLocaleDateString() : 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={3} sx={{ textAlign: 'right' }}>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ color: getTransactionColor(transaction.type?.value || '') }}
                                            >
                                                {formatCurrency(transaction.amount || 0)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Balance: {formatCurrency(transaction.outstandingLoanBalance || 0)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default RecentTransactions; 